import gsap from 'gsap';
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
gsap.registerPlugin(ScrollToPlugin);

let sectionNav;

// The 2026 header is `id="siteheader"` (templates/_partials/header2026/header
// .twig); the pre-2026 one was `id="header"`. This was still looking up the old
// id and dereferencing the result unguarded — and because both the lookup and
// ScrollTrigger.create()'s first `start()` call happen during module
// evaluation, the resulting TypeError aborted main.js BEFORE it registered its
// `window load` handler. That silently killed search, team, both carousels, the
// home hero, `featured` and `map.init()` on every page carrying a
// `.js-scroll-section` — including this one, which is why the field guide map
// never left its loading spinner. Resolve either id, and never assume one.
const header = document.getElementById('siteheader') || document.getElementById('header');

function calcScrollOffset() {
  let offset = header ? header.offsetHeight - 1 : 0;

  if (sectionNav) {
    const top = parseFloat(window.getComputedStyle(sectionNav).top);
    offset = offset + top;
  }
  
  return offset;
}

// Sections that have a link in the sticky bar, in document order.
let spied = [];
let queued = false;

/**
 * Marks the one section the reader is in.
 *
 * Each section used to own its own link and toggle it on `onEnter` /
 * `onLeaveBack`, which meant nothing decided which single link was current.
 * Three things fell out of that: at the top of the page no link was marked at
 * all, because the first section's top had not yet crossed the line; every
 * section you scrolled past stayed marked, so several were underlined at once;
 * and a last section too short to reach the line was never marked however far
 * you scrolled. One place picks the current section now, and the rest are
 * cleared against it.
 */
function updateActive() {
  queued = false;
  if (!spied.length) return;

  // The slack matters. A jump lands a section's top about 16px below the line,
  // because the offset is worked out while the header is still at its full
  // height and the page settles with it stuck and 16px shorter. At 2px of
  // tolerance the bar you just clicked never lit up.
  const line = calcScrollOffset() + 24;
  const doc = document.documentElement;
  const atBottom = window.innerHeight + window.scrollY >= doc.scrollHeight - 2;

  // Above the first section, the first link is still where the reader is
  // heading, so it stays marked rather than nothing being marked.
  let current = spied[0];

  if (atBottom) {
    // The page can end before the last section's top reaches the line.
    current = spied[spied.length - 1];
  } else {
    spied.forEach((item) => {
      if (item.section.getBoundingClientRect().top <= line) current = item;
    });
  }

  spied.forEach((item) => {
    item.link.classList.toggle('section-is-scrolled', item === current);
    // Colour was the only thing saying which section you were in.
    if (item === current) {
      item.link.setAttribute('aria-current', 'true');
    } else {
      item.link.removeAttribute('aria-current');
    }
  });
}

function queueUpdate() {
  if (queued) return;
  queued = true;
  requestAnimationFrame(updateActive);
}

function handleLink(e) {
  e.preventDefault();
  let selector = e.currentTarget.getAttribute('href');
  const el = document.querySelector(selector);
  if (el) {
    gsap.to(window, {
      duration: 1,
      scrollTo: {
        y: el,
        offsetY: () => {
          return calcScrollOffset();
        },
      },
      ease: "power2.inOut"
    });
  }
}

// Init sections is for the sticky in-page nav
function initSections() {
  spied = Array.from(document.querySelectorAll('.js-scroll-section'))
    .map((section) => ({
      section,
      link: section.id
        ? document.querySelector(`.js-nav-link[href="#${section.id}"]`)
        : null,
    }))
    .filter((item) => item.link);

  if (!spied.length) return;

  window.addEventListener('scroll', queueUpdate, { passive: true });
  window.addEventListener('resize', queueUpdate);
  updateActive();
}

// This is for detecting any other links on the page that could be in-page scroll-to links
function initInPageLinks() {
  const links = document.querySelectorAll('a[href^="#"]');
  links.forEach(link => {
    link.addEventListener('click', handleLink);
  });
}

export default function() {
  sectionNav = document.querySelector('#hub-nav') || document.querySelector('#section-nav');
  initSections();
  initInPageLinks();
}
